import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import { Icon, IconChat, IconClosedCaption, IconCrown, IconLiveStreaming, IconMicrophone, IconModerator, IconPhone, IconPolls, IconRecording, IconSecurityOn, IconSettings, IconShareDesktop, IconSwitchCamera } from '../../base/icons';
import { PERMISSIONS, PERMISSION_LIST, getEffectiveParticipantPermissions } from '../../base/permissions';
import { Tooltip } from '../../base/tooltip';
/**
 * The icon that stands for each capability in the list.
 */
const PERMISSION_ICONS = {
    [PERMISSIONS.SEND_MEDIA]: IconMicrophone,
    [PERMISSIONS.SHARE_SCREEN]: IconShareDesktop,
    [PERMISSIONS.SEND_GROUPCHAT]: IconChat,
    [PERMISSIONS.CREATE_POLLS]: IconPolls,
    [PERMISSIONS.RECORDING]: IconRecording,
    [PERMISSIONS.LIVESTREAMING]: IconLiveStreaming,
    [PERMISSIONS.TRANSCRIPTION]: IconClosedCaption,
    [PERMISSIONS.OUTBOUND_CALL]: IconPhone,
    [PERMISSIONS.FLIP]: IconSwitchCamera,
    [PERMISSIONS.MODERATE_PARTICIPANTS]: IconModerator,
    [PERMISSIONS.GRANT_ROLES]: IconCrown,
    [PERMISSIONS.MANAGE_ROOM]: IconSettings
};
/**
 * A shield in the row's icon stack that opens, on click, the list of every
 * capability the person holds or lacks: what their role grants, minus what the
 * meeting's live state (block-all, force locks, AV moderation, screen-share and
 * recording policies) currently takes away. Click rather than hover, so a
 * twelve-row list is readable without the pointer having to stay put, and so
 * it works on touch.
 *
 * @param {IProps} props - Component props.
 * @returns {JSX.Element}
 */
const ParticipantPermissionsIndicator = ({ firstInList, participant, permissions }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [anchor, setAnchor] = useState(null);
    const rootRef = useRef(null);
    // shallowEqual: the selector builds a fresh map, and a row must not
    // re-render for every unrelated store update.
    const derived = useSelector((state) => permissions !== null && permissions !== void 0 ? permissions : getEffectiveParticipantPermissions(state, participant), shallowEqual);
    const denied = PERMISSION_LIST.filter(permission => !derived[permission]).length;
    // The list is rendered through a portal, positioned from the badge's
    // on-screen rectangle: every ancestor of a pane row clips its overflow, so
    // a popover parked inside the row would be cut off at the row's edge.
    const toggle = useCallback((e) => {
        var _a, _b;
        e.stopPropagation();
        setAnchor((_b = (_a = rootRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect()) !== null && _b !== void 0 ? _b : null);
        setOpen(value => !value);
    }, []);
    const onKeyPress = useCallback((e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            toggle(e);
        }
    }, [toggle]);
    // Close on a click anywhere else, on Escape, and when anything scrolls
    // (the list is anchored to where the badge was when it opened).
    useEffect(() => {
        if (!open) {
            return;
        }
        const onDocumentClick = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                setOpen(false);
            }
        };
        const onScroll = () => setOpen(false);
        document.addEventListener('click', onDocumentClick);
        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('scroll', onScroll, true);
        return () => {
            document.removeEventListener('click', onDocumentClick);
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('scroll', onScroll, true);
        };
    }, [open]);
    // Below the badge for the first row of a list (the pane header is right
    // above it), above the badge for every other row.
    const popoverStyle = anchor
        ? firstInList
            ? {
                right: window.innerWidth - anchor.right,
                top: anchor.bottom + 6
            }
            : {
                bottom: window.innerHeight - anchor.top + 6,
                right: window.innerWidth - anchor.right
            }
        : undefined;
    return (React.createElement("div", { className: 'participant-permissions-root', ref: rootRef },
        React.createElement(Tooltip, { className: 'small', content: 'participantsPane.permissions.title', position: firstInList ? ' bottom-right' : 'top' },
            React.createElement("div", { "aria-expanded": open, "aria-label": t('participantsPane.permissions.title'), className: denied
                    ? 'moderator-icon-participants participantpane-item-icons participant-permissions-badge'
                        + ' participant-permissions-badge-restricted'
                    : 'moderator-icon-participants participantpane-item-icons participant-permissions-badge', onClick: toggle, onKeyPress: onKeyPress, role: 'button', tabIndex: 0 },
                React.createElement(Icon, { src: IconSecurityOn }))),
        open && anchor && ReactDOM.createPortal(React.createElement("div", { className: 'participant-permissions', role: 'dialog', style: popoverStyle },
            React.createElement("span", { className: 'participant-permissions-title' }, t('participantsPane.permissions.title')),
            React.createElement("ul", { className: 'participant-permissions-list' }, PERMISSION_LIST.map(permission => (React.createElement("li", { className: derived[permission]
                    ? 'participant-permissions-item'
                    : 'participant-permissions-item participant-permissions-item-denied', key: permission },
                React.createElement(Icon, { size: 12, src: PERMISSION_ICONS[permission] }),
                React.createElement("span", null, t(`participantsPane.permissions.${permission}`)),
                React.createElement("span", { className: 'participant-permissions-state' }, t(derived[permission]
                    ? 'participantsPane.permissions.allowed'
                    : 'participantsPane.permissions.denied'))))))), document.body)));
};
export default ParticipantPermissionsIndicator;
